import { action, computed, observable, reaction, runInAction } from 'mobx';
import { getUniversalCookies } from '../util/CookiesUtils';
import Configuracao from '../util/Configuracao';

import ApiFactory from '../api/ApiFactory';
import LoginService from '../services/LoginService';
import UsuarioService from '../services/UsuarioService';
import SessionStorageUtil from '../util/SessionStorageUtil';
import { mensagemStore } from '../store/stores';

const ALL_ROLES = [
  'administrador_plano',
  'gestor',
  'auditor_med_autorizacao',
  'auditor_med_faturamento',
  'auditor_med_auxiliar',
  'auditor_med_externo',
  'gestor_autorizacao',
  'gestor_fatura',
  'perito_odontologico',
  'gestor_fatura_odonto',
  'gestor_autorizacao_odonto',
  'prestador',
  'prestador_faturamento',
  'prestador_autorizacao',
  'prestador_medico',
  'prestador_odontologico',
  'prestador_medico_autorizacao',
  'prestador_odontologico_autorizacao',
  'prestador_medico_faturamento',
  'prestador_odontologico_faturamento',
  'usuario_consulta',
  'reembolso_consulta',
].map(r => r.toUpperCase());

const axios = require('axios');
const CNPJ_UNIMED = '02812468000106';

/**
 * Classe utilizada para guardar e manipular o estado da aplicação.
 * Deve-se criar uma para cada contexto ou entidade.
 */

class UsuarioStore {
  // Usuário logado no sistema
  @observable usuarioSso;
  // Usuário da Aplicação com Nome, Código e Roles
  @observable usuarioAplicacao;
  @observable isUsuarioPrestador;
  @observable isPrestadorUnimed;
  // @observable isUsuarioBeneficiario;
  @observable isUsuarioEmAlgumaRole;
  @observable isUsuarioGestor;
  @observable isUsuarioCAUPE;

  // Salva o path do navegador para redicionar o usuário
  // quando ocorrer erro 401 ou 403
  @observable previousPath;

  loginService = new LoginService();
  usuarioService = new UsuarioService();

  constructor (storage) {
    try {
      let ssoToken = getUniversalCookies().get('ssoToken');
      if (ssoToken !== undefined) {
        let usuarioSso = {};
        if (ssoToken) {
          let tokenArray = ssoToken.split('.');
          if (tokenArray.length === 3) {
            usuarioSso = JSON.parse(atob(tokenArray[1]));
            usuarioSso.ssoToken = ssoToken;
            (async () => {
              await this.preencherObservables(usuarioSso.codigoUsuario);
            })();
            runInAction(() => {
              this.usuarioSso = usuarioSso;
            });
            if (this.isUsuarioLogado) {
              mensagemStore.abrirMensagem('Bem-vindo ao sistema TST-Saúde 3.', 3000, 'success');
            } else {
              mensagemStore.abrirMensagem('Favor realizar o login no sistema.', 3000, 'info');
            }
          }
        }
      }
    } catch (e) {
      console.error('erro ao recuperar cookie', e);
    }
    if (storage !== null && this.usuarioSso === undefined && storage.usuarioSso !== undefined) {
      runInAction(() => {
        this.usuarioSso = storage.usuarioSso;
      });
    }
    SessionStorageUtil.autoSave(this, this.save.bind(this));
  }

  reactionUsuario = reaction(() => this.usuarioSso,
    (usuarioSso) => {
      if (usuarioSso) {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + usuarioSso.ssoToken;
      }
      // Configura o usuário na ApiFactory para que seja utilizado no header das requisições do backend
      ApiFactory.reset(usuarioSso);
    });

  @action saveLocationOnError = (path) => {
    runInAction(() => {
      this.previousPath = path;
    });
  }
  @action resetPreviousPath = () => {
    this.previousPath = undefined;
  }

  @action reset = () => {
    this.usuarioSso = undefined;
    this.usuarioAplicacao = undefined;
    this.isUsuarioPrestador = undefined;
    this.isUsuarioEmAlgumaRole = undefined;
    this.isPrestadorUnimed = undefined;
    this.isUsuarioGestor = undefined;
    this.isUsuarioCAUPE = undefined;
    this.save({}); // Remove o usuário da sessão
  }

  save (json) {
    SessionStorageUtil.setItem('UsuarioStore', json);
  }

  @action async realizarLogin (codUsuario, senha) {
    let usuarioSso = await this.loginService.realizarLogin(codUsuario, senha);
    await this.preencherObservables(codUsuario);
    runInAction(() => {
      this.usuarioSso = usuarioSso;
    });

    if (this.isUsuarioLogado) {
      mensagemStore.abrirMensagem('Bem-vindo ao sistema TST-Saúde 3.', 3000, 'success');
    } else {
      mensagemStore.abrirMensagem('Favor realizar o login no sistema.', 3000, 'info');
    }
  }

  @action async preencherObservables (codUsuario) {
    if (this.usuarioAplicacao === undefined) {
      let usuarioAplicacao = await this.usuarioService.recuperarUsuarioAplicacao(codUsuario);

      console.log(usuarioAplicacao);

      let isUsuarioEmAlgumaRole = this._isUsuarioEmAlgumaRole(usuarioAplicacao);
      let isUsuarioPrestador = this._isUsuarioPrestador(usuarioAplicacao);
      let prestador = isUsuarioPrestador ? usuarioAplicacao.usuarioPrestador : {};
      let isPrestadorUnimed = isUsuarioPrestador && prestador.cpfCnpj === CNPJ_UNIMED;
      let isUsuarioGestor = this._isUsuarioGestor(usuarioAplicacao);
      let isUsuarioCAUPE = this._isUsuarioCAUPE(codUsuario);

      runInAction(() => {
        this.usuarioAplicacao = usuarioAplicacao;
        this.isUsuarioPrestador = isUsuarioPrestador;
        this.isUsuarioEmAlgumaRole = isUsuarioEmAlgumaRole;
        this.prestador = isUsuarioPrestador ? prestador : null;
        this.isPrestadorUnimed = isPrestadorUnimed;
        this.isUsuarioGestor = isUsuarioGestor;
        this.isUsuarioCAUPE = isUsuarioCAUPE;
      });
    }
  }

  @action realizarLogoff = async () => {
    const { domain } = Configuracao.getConfiguracao().configuracao;
    getUniversalCookies().remove('ssoToken', { path: '/', domain: domain || '.rede.tst' });
    delete axios.defaults.headers.common['Authorization'];
    this.reset();
  }

  _isUsuarioEmAlgumaRole = (usuarioAplicacao) => {
    return usuarioAplicacao !== undefined && usuarioAplicacao.roles && usuarioAplicacao.roles.some(role => ALL_ROLES.indexOf(role.toUpperCase()) > -1);
  }

  _isUsuarioGestor = (usuarioAplicacao) => {
    const GESTOR = ALL_ROLES[1];
    return usuarioAplicacao !== undefined && usuarioAplicacao.roles && usuarioAplicacao.roles.indexOf(GESTOR.toUpperCase()) > -1;
  }

  _isUsuarioPrestador = (usuarioAplicacao) => {
    return usuarioAplicacao !== undefined && usuarioAplicacao.usuarioPrestador !== null && usuarioAplicacao.prestador;
  }

  _isUsuarioCAUPE = (codUsuario) => {
    return ['C049870', 'C033182', 'C037940', 'C041415', 'C040820', 'C060420', 'C031277', 'C031150', 'C033075', 'C057139'].includes(codUsuario);
  }

    @computed get isUsuarioLogado () {
    return this.usuarioSso !== undefined;
  }

    @computed get codigoUsuario () {
      return this.usuarioSso.codigoUsuario;
    }

    @computed get nomeUsuario () {
      return this.usuarioAplicacao !== undefined ? this.usuarioAplicacao.nome : '';
    }

    @computed get isPrestador () {
      return this.isUsuarioPrestador;
    }

    @computed get isEmAlgumaRole () {
      return this.isUsuarioEmAlgumaRole;
    }

    // @computed get isBeneficiario () {
    //   return this.isUsuarioBeneficiario;
    // }

    @computed get isUnimed () {
      return this.isPrestadorUnimed;
    }

    @computed get isGestor () {
      return this.isUsuarioGestor;
    }

    @computed get isCAUPE () {
      return this.isUsuarioCAUPE;
    }

    @computed get codPrestador () {
      return this.prestador.codPrestador;
    }

    @computed get nomPrestador () {
      return this.prestador.nomPrestador;
    }

    @computed get cpfCnpj () {
      return this.prestador.cpfCnpj;
    }
}

export default UsuarioStore;
